import LoadService from 'services/loader-handler-services/load-service';
import CourseSettingsService from 'services/course-handler-services/course-settings-service';
import EnrollmentDataService from 'services/data-handler-services/enrollment-data-service';
import { LocalStorageService, SessionStorageService } from 'services/data-handler-services/web-storage-service';
import utils from 'lib/utils';
import EF from 'ef/ef';
import { localize } from 'pseudo-localization';
import i18next from 'i18next';
import i18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import _ from 'lodash';
import { match } from 'minta';

const LocaleService = function () {
  if (LocaleService.prototype._singleton) {
    return LocaleService.prototype._singleton;
  }

  LocaleService.prototype._singleton = this;

  this.files = {
    _localeContent: './content/locales/{{locale}}.json',
    _langContent: './content/locales/{{lang}}.json',
  };

  this.fallbackNSes = ['course', 'sdk'];
  this.moduleNS = null;
  this.initialize = function (options) {
    return new Promise((resolve, reject) => {
      const split = CourseSettingsService.get('split_module');
      this.courseSupLanguages = CourseSettingsService.get('supported_languages');
      this.fallbackLng = 'en';
      this.files = {};
      this.filePaths = {
        _localeContent: split ? `./modules/${split}/locales/{{locale}}.json` : './content/locales/{{locale}}.json',
        _langContent: split ? `./modules/${split}/locales/{{lang}}.json` : './content/locales/{{lang}}.json',
      };

      // Check if the options language is set up, format it if it is declared with other nomenclature
      // otherwise it will be undefined and use the fallback declared on the i18next object
      if (options.lng && !this.courseSupLanguages.includes(options.lng)) {
        if (options.lng.indexOf('-') === 2) {
          options.lng = this.courseSupLanguages.includes(options.lng.substring(0, 2))
            ? options.lng.substring(0, 2)
            : undefined;
        } else {
          options.lng = undefined;
        }
      }

      const i18nextOptions = _.defaults(
        {
          fallbackLng: this.fallbackLng,
          fallbackNS: this.fallbackNSes,
          keySeparator: '.',
          nsSeparator: '<|>',
          detection: {
            order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
            lookupQuerystring: 'setLng',
            lookupCookie: 'i18next',
            lookupLocalStorage: 'i18nextLng',
          },
          interpolation: { escapeValue: false },
          supportedLngs: this.courseSupLanguages,
          interpolationPrefix: '{{',
          interpolationSuffix: '}}',
          customLoad: function (lng, ns, options, loadComplete) {
            //we're loading the files ourselves, don't need i18next to do it;
            // can't find another way of turning this off...
            loadComplete(null, null);
          },
        },
        options
      );

      i18next.use(i18nextBrowserLanguageDetector).init(i18nextOptions, () => {
        const i18nextLang = i18next.language;
        this.setFileConfigData(i18nextLang);
        this.loadLanguageFileData(this.files)
          .then(resolve)
          .catch(() => {
            this.setFileConfigData(this.fallbackLng);
            this.loadLanguageFileData(this.files)
              .then(() => {
                // eslint-disable-next-line no-console
                console.info('Fallback language loaded successfully');
                resolve();
              })
              .catch(() => {
                const errMessage = "Couldn't load the fallback language";
                // eslint-disable-next-line no-console
                console.info(errMessage);
                reject(errMessage);
              });
            throw Error("Couldn't load the desired language");
          })
          .catch(({ message }) => {
            // eslint-disable-next-line no-console
            console.info(message);
          });
      });
    });
  };

  this.setFileConfigData = function (i18nextLang) {
    if (i18nextLang.indexOf('-') === 2) {
      this.lang = i18nextLang.substring(0, 2);
      this.locale = i18nextLang;
      this.files._localeContent = this.filePaths._localeContent.replace('{{locale}}', this.locale);
    } else {
      this.lang = i18nextLang;
      this.locale = undefined;
      delete this.files._localeContent;
    }
    this.files._langContent = this.filePaths._langContent.replace('{{lang}}', this.lang);

    if (i18nextLang !== 'en') {
      this.files._enContent = './content/locales/en.json';
    }
  };

  this.loadLanguageFileData = function (filesConfig) {
    return LoadService.load(filesConfig).then(data => {
      Object.defineProperty(this, 'localeContent', {
        get: function () {
          return data._localeContent;
        },
      });
      Object.defineProperty(this, 'langContent', {
        get: function () {
          return data._langContent;
        },
      });
      Object.defineProperty(this, 'enContent', {
        get: function () {
          return data._enContent;
        },
      });
      _.each(this.fallbackNSes, ns => {
        this.addResources(ns);
      });
    });
  };

  this.addDefaultResources = function (namespace, subView) {
    const prefix = EnrollmentDataService.getData('locale:namespace:prefix');
    const prefixedNameSpace = prefix ? `${prefix}|${namespace}` : namespace;

    this.addResources(namespace);

    if (prefix) {
      this.addResources(prefixedNameSpace);
    }

    if (this.moduleNS) {
      if (!subView) {
        if (prefix) {
          i18next.options.fallbackNS = [prefixedNameSpace, namespace].concat([this.moduleNS].concat(this.fallbackNSes));
        } else {
          i18next.options.fallbackNS = [namespace].concat([this.moduleNS].concat(this.fallbackNSes));
        }
      } else if (i18next.options.fallbackNS.indexOf(namespace) === -1) {
        i18next.options.fallbackNS = [namespace].concat(i18next.options.fallbackNS);
      }
    }

    if (!subView) {
      i18next.setDefaultNamespace(prefixedNameSpace);
    }
  };

  this.setLocaleNamespacePrefix = function (prefix) {
    EnrollmentDataService.saveData('locale:namespace:prefix', prefix);
  };

  this.setModuleResources = function (moduleKey) {
    const ns = `${moduleKey}.defaults`;
    this.addResources(ns);
    this.moduleNS = ns;
    //overwrite other fallbacks when starting a new module
    i18next.options.fallbackNS = [ns].concat(this.fallbackNSes);
  };

  this.addResources = function (namespace) {
    //en should always be present
    if (!i18next.hasResourceBundle('en', namespace)) {
      this.addResource(this.lang, namespace, this.langContent);

      if (this.locale) {
        this.addResource(this.locale, namespace, this.localeContent);
      }

      if (i18next.language !== 'en') {
        this.addResource('en', namespace, this.enContent);
      }
    }
  };

  this.currentNamespace = function () {
    return i18next.options.defaultNS;
  };

  this.addResource = function (lang, namespace, content) {
    const resource = utils.dotStringToObject(namespace, content);
    i18next.addResourceBundle(lang, namespace, resource);
  };

  this.shouldPseudoLocalize = function (key, data) {
    if (_.isNil(key) || !EF.urlHelper) {
      return false;
    }
    return EF.urlHelper.getParam('pseudoLocalize', false) && i18next.exists(String(key), data);
  };

  this.t = function (key, options) {
    let result = '';
    match(key)(
      /^customkey:\/\//,
      () => {
        result = utils.renderMarkdown(
          EF.customizations.getByKeyAndSlug('text-content-customization', key.replace(/^customkey:\/\//, ''))
            ?.custom_text
        );
      },
      /^enrollmentkey:\/\//,
      () => {
        result = EnrollmentDataService.getByDotString(key.replace(/^enrollmentkey:\/\//, ''));
      },
      /^localkey:\/\//,
      () => {
        result = LocalStorageService.getByDotString(key.replace(/^localkey:\/\//, ''));
      },
      /^sessionkey:\/\//,
      () => {
        result = SessionStorageService.getByDotString(key.replace(/^sessionkey:\/\//, ''));
      },
      otherwise => {
        const sessionData = SessionStorageService.getAll();
        const model = CourseSettingsService.getModel();
        const courseData = model ? model.toJSON() : {};
        const data = _.extend(sessionData, courseData, options);
        const string = i18next.t(key, data);
        result = this.shouldPseudoLocalize(key, data) ? localize(string) : string;
      }
    );
    return result || '';
  };

  this.getLanguage = function () {
    return i18next.language;
  };

  this.isRTL = function () {
    switch (i18next.language) {
      case 'ar': // Arabic
      case 'he': // Hebrew
      case 'ur': // Urdu
        return true;
      default:
        return false;
    }
  };
  
  this.getResourceByNamespace = function (namespace) {
    return i18next.getResourceBundle(i18next.language, namespace);
  };
};

export default new LocaleService();
